import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, Input, Space, Table } from 'antd';
import type { TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { AsyncBoundary } from '../AsyncBoundary';
import { useCapabilityContext } from '../capability/CapabilityContext';
import { useResizableColumns } from '../ResizableColumns';
import {
  cellTextFor,
  comparatorFor,
  defaultWidthFor,
  filterConfigFor,
  rendererFor,
  type DataColumn,
} from './columnDefaults';
import { DEFAULT_PAGE_SIZE, type TableState } from './useUrlTableState';

/**
 * The shared table primitive. Sorting, filtering, search, pagination,
 * row-click navigation, density and empty/loading/error states are DEFAULTS —
 * opt out per column, never opt in. That inversion is the whole point: the
 * repo had 19 hand-rolled <Table> sites whose sort coverage ranged from six
 * columns to zero, because each one re-declared everything by hand.
 *
 * OWNERSHIP: DataTable is the sole owner of sort, filter, search and
 * pagination. Refine's `tableProps` carries its own `pagination` and
 * `onChange`, which are deliberately DISCARDED — honouring them would put two
 * controllers on the same state. Only `dataSource` and `loading` are consumed.
 * Call sites pass `pagination: { mode: 'off' }` to Refine so it fetches
 * without slicing.
 *
 * No @refinedev/* import and no react-router import may enter this file:
 * `tableProps` is consumed STRUCTURALLY (it is just antd props), which is
 * exactly how this stays Refine-free.
 *
 * CONTROLLED OR UNCONTROLLED: supplying `onStateChange` makes the table
 * controlled — `state` is then the single source of truth and no shadow state
 * is kept. Omitting it makes the table uncontrolled: state lives in internal
 * `useState`, still seeded by `defaultState`. Both modes behave identically to
 * the user; uncontrolled exists so a modal or embedded table need not lift
 * state it has nowhere to put. A `state` prop passed WITHOUT `onStateChange`
 * is not honoured — use `defaultState` to seed an uncontrolled table. The
 * mirror case is just as inert: `onStateChange` WITHOUT `state` makes the
 * table controlled by a source of truth that never arrives, so every sort,
 * filter and page click fires the callback and changes nothing on screen.
 * Supply both, or neither.
 *
 * TWO BEHAVIORS THAT SURPRISE PEOPLE:
 *
 *  - Filter dropdown options are derived from the POST-SEARCH row set
 *    (`filterConfigFor(col, searched)`), so typing in the search box narrows
 *    the values offered in each column's filter list. That is intended: the
 *    dropdown offers what is actually reachable, never a value that would
 *    return nothing.
 *
 *  - `searchFields` matches a column by IDENTITY on `dataIndex` or `key`
 *    (`c.dataIndex === f || c.key === f`). A column whose `dataIndex` is an
 *    array path (e.g. `['owner', 'name']`) will therefore not match, and the
 *    field falls back to a raw top-level property read on the record. Give
 *    such a column a `key` equal to the search field, or a `projectValue`, if
 *    it needs to search on the nested value.
 */
export interface DataTableProps<T> {
  /** Refine interop. ONLY `dataSource` and `loading` are consumed. */
  tableProps?: Pick<TableProps<T>, 'dataSource' | 'loading'>;
  dataSource?: T[];
  loading?: boolean;
  columns: DataColumn<T>[];
  rowKey: (keyof T & string) | ((record: T) => string);
  /** Persistence key for column widths. Required — no shared default. */
  storageKey: string;
  /**
   * Optional context signal. Changing it DROPS the sticky rows (see the
   * `stickyRowsRef` in the body) so a genuine dataset SWAP shows a fresh load
   * instead of holding the previous context's rows. The console passes the
   * active tenant scope here: switching tenant refetches a different, possibly
   * smaller row set, and without this the table would hold the old tenant's
   * larger list and then snap to the new one. Leave unset when the row set
   * never changes underneath the table — a filter of the SAME query keeps a
   * stable `resetKey` and so still benefits from the no-blank sticky behaviour.
   */
  resetKey?: unknown;

  /** Controlled state. Only honoured alongside `onStateChange`. */
  state?: TableState;
  /** Supplying this makes the table controlled; omitting it makes it uncontrolled. */
  onStateChange?: (next: TableState) => void;
  /**
   * Seeds state when nothing is set. Replaces per-column defaultSortOrder.
   *
   * Expected to be a STABLE reference (a module-level constant, or memoized by
   * the caller). An inline object literal is a new identity every render, which
   * busts the `effective` memo and, through it, `prepared` and `finalColumns` —
   * so every column is rebuilt on every render for no reason.
   * `useUrlTableState` documents the same expectation for its own
   * `opts.defaultState`.
   */
  defaultState?: Partial<TableState>;

  onRowClick?: (record: T) => void;
  rowClickExcludeKeys?: string[];

  searchFields?: (keyof T & string)[];
  searchPlaceholder?: string;
  /** Server mode. Takes precedence — no client filtering happens. */
  onSearch?: (term: string) => void;

  pagination?: false | { pageSize?: number; showSizeChanger?: boolean };

  empty?: { title?: string; description?: ReactNode; action?: ReactNode };
  error?: unknown;
  onRetry?: () => void;

  density?: 'default' | 'compact';
  scrollX?: number | 'max-content' | false;
}

/** Long enough not to thrash the URL, short enough to feel instant. */
const SEARCH_DEBOUNCE_MS = 250;

/** antd's `onHeaderCell` signature, extracted so merged callbacks stay typed. */
type HeaderCellFn<T> = NonNullable<DataColumn<T>['onHeaderCell']>;

/**
 * The ONE derivation of the key a column is tracked by in `TableState`.
 *
 * It has to be one function because antd reports a sort back through two
 * different fields — `columnKey: column.key` and `field: column.dataIndex`
 * (antd/es/table/hooks/useSorter.js) — while `sortOrder`/`filteredValue` are
 * looked up from `col.key ?? col.dataIndex`. If the write path and the read
 * path normalise differently, a sort is stored under one spelling and read
 * under another, and the column silently never shows as sorted. An array
 * `dataIndex` (a nested path) joins on `.` rather than falling through to
 * `String([...])`, whose comma join is an artifact of `Array.prototype.join`'s
 * default rather than a deliberate choice.
 */
function stateKeyOf(k: unknown): string {
  if (Array.isArray(k)) return k.join('.');
  return String(k ?? '');
}

export function DataTable<T extends object>({
  tableProps,
  dataSource,
  loading,
  columns,
  rowKey,
  storageKey,
  resetKey,
  state,
  onStateChange,
  defaultState,
  onRowClick,
  rowClickExcludeKeys,
  searchFields,
  searchPlaceholder = 'Search',
  onSearch,
  pagination,
  empty,
  error,
  onRetry,
  density = 'default',
  scrollX = 'max-content',
}: DataTableProps<T>) {
  // Read the capability predicate ONCE, never per row or per item.
  const { can } = useCapabilityContext();

  // Controlled iff the caller supplied `onStateChange`. When it did, the
  // internal state below is never read or written, so there is no shadow copy
  // to drift. When it did not, the table drives itself — without this, a
  // caller that omitted both props got headers advertising `column-has-sorters`
  // and a pager, where every click was a no-op.
  const [internalState, setInternalState] = useState<TableState>({});
  const tableState = onStateChange ? state : internalState;
  const setTableState = useCallback(
    (next: TableState) => {
      if (onStateChange) onStateChange(next);
      else setInternalState(next);
    },
    [onStateChange],
  );

  const effective: TableState = useMemo(
    () => ({ ...defaultState, ...tableState }),
    [defaultState, tableState],
  );

  // antd types `loading` as `boolean | SpinProps`, but AsyncBoundary takes a
  // boolean. A SpinProps object means "loading" unless it says otherwise.
  const rawLoading = loading ?? tableProps?.loading ?? false;
  const isLoading =
    typeof rawLoading === 'object' ? (rawLoading.spinning ?? true) : rawLoading;

  const rawRows = (dataSource ?? tableProps?.dataSource ?? []) as T[];
  // STICKY ROWS — the filter-flicker fix. On-device render instrumentation
  // proved the cause: a filter interaction triggers a Refine refetch and
  // `tableProps.dataSource` drops to EMPTY for a frame during it, so the whole
  // table blanks (a skeleton, THEN an empty body) and refills — the redraw the
  // user sees. Hiding the skeleton alone was NOT enough; the rows themselves
  // still vanished. So hold the last non-empty row set and keep rendering it
  // through a transient loading-blank. The real rows are used again the instant
  // loading finishes, so a genuinely-empty result still shows its empty state.
  const stickyRowsRef = useRef<T[]>([]);
  const prevResetKeyRef = useRef(resetKey);
  if (prevResetKeyRef.current !== resetKey) {
    // A caller-signalled context change (the console tenant-scope switch): the
    // incoming rows are a DIFFERENT set, not the same data mid-refetch. Drop the
    // held rows so the fresh load shows its skeleton instead of the previous
    // context's rows snapping to the new set. Filter interactions keep a stable
    // resetKey, so they still get the no-blank sticky behaviour above.
    prevResetKeyRef.current = resetKey;
    stickyRowsRef.current = [];
  }
  if (rawRows.length > 0) stickyRowsRef.current = rawRows;
  const rows: T[] =
    isLoading && rawRows.length === 0 ? stickyRowsRef.current : rawRows;

  // The search box holds its own value between debounces so typing stays
  // responsive even though committing the term re-renders the table.
  const [searchDraft, setSearchDraft] = useState(effective.search ?? '');

  // In server mode the committed term lives HERE, not in `effective` — the
  // server owns the row set, so nothing writes it back into table state. It is
  // state rather than a ref because two render-time decisions read it: the
  // debounce guard below, and `hasNarrowing`. Seeded from `effective.search`
  // so a URL-seeded term does not fire a redundant `onSearch` on mount.
  const [serverSearch, setServerSearch] = useState(() => effective.search ?? '');

  // The last term THIS component committed. Any other value arriving in
  // `committed` is therefore an external update (browser Back, a reset button,
  // a link into the same route with a different `?q=`), which must resync the
  // box rather than be written back over. See the effect below.
  const lastCommittedRef = useRef(effective.search ?? '');

  const commitSearch = useCallback(
    (term: string) => {
      // Claim the term BEFORE writing it, so the re-render this write causes
      // is recognised as our own rather than as an external change.
      lastCommittedRef.current = term;
      if (onSearch) {
        setServerSearch(term);
        // Any change to the row set resets to page 1 — otherwise a user on
        // page 4 who types a search lands on an empty table. The server owns
        // the rows in this mode, so the TERM is not written into table state,
        // but the page still is: the contract holds in both modes. Guarded,
        // because an unconditional write would push a history entry per
        // committed keystroke in a `useUrlTableState` caller.
        if ((effective.page ?? 1) !== 1) setTableState({ ...effective, page: 1 });
        onSearch(term);
        return;
      }
      setTableState({ ...effective, search: term || undefined, page: 1 });
    },
    [onSearch, setTableState, effective],
  );

  // A pending debounce must commit against the LATEST state, not the state
  // captured when the keystroke landed. Without this ref, a user who types and
  // then sorts within the debounce window fires a commit built from a stale
  // `effective` — and since `encodeTableState` clears this table's params
  // before writing, that stale object deletes the sort they just applied.
  // `useUrlTableState`'s own JSDoc flags this exact interleaving.
  const commitRef = useRef(commitSearch);
  commitRef.current = commitSearch;

  // Debounce the draft into committed state. This is what makes the box filter
  // as you type: antd's <Input.Search onSearch> fires only on Enter or the
  // button, which would leave typed text doing nothing until submitted.
  //
  // `committed` must be the REAL last-committed term in both modes. Deriving
  // it as `undefined` in server mode made this guard read `searchDraft === ''`,
  // which is exactly true when the user backspaces the box empty — so
  // `onSearch('')` never fired and the server-side filter stayed applied
  // behind an empty search box.
  //
  // `committed` is a dependency, which makes an EXTERNAL change to it — one
  // this component did not make — re-run the effect with a stale draft. That
  // has to resync rather than commit. Concretely, with `useUrlTableState`: the
  // user types `acme` (URL `?q=acme`), then presses Back; `state.search` goes
  // away, `committed` flips to '' while `searchDraft` is still `acme`, and a
  // commit here would re-write `?q=acme` with `replace: true` 250 ms later,
  // clobbering the history entry Back had just restored. Back would appear to
  // do nothing at all.
  const committed = onSearch ? serverSearch : (effective.search ?? '');
  useEffect(() => {
    if (committed !== lastCommittedRef.current) {
      lastCommittedRef.current = committed;
      setSearchDraft(committed);
      return;
    }
    if (searchDraft === committed) return;
    const t = setTimeout(() => commitRef.current(searchDraft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchDraft, committed]);

  // Authoring warnings, keyed on `columns` so they fire once per column set.
  // They lived inside the `prepared` memo, whose deps include `effective` —
  // which meant they re-fired on every sort, filter, page and search change,
  // and twice over under StrictMode.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    for (const col of columns) {
      const colKey = stateKeyOf(col.key ?? col.dataIndex);
      // `comparatorFor` never reads `col.sorter` — the supported escape hatch
      // is `sortable`. A column converted from a hand-rolled <Table> that
      // still carries `sorter:` would otherwise lose it in silence.
      if (col.sorter !== undefined) {
        console.warn(
          `DataTable: column "${colKey}" supplies \`sorter\`, which DataTable ignores. ` +
            'Use `sortable` (a comparator, or false to opt out) instead.',
        );
      }
      // `key` is optional on antd's ColumnType, so nothing stops a column
      // being defined with only a `dataIndex` — but antd then reports its
      // sort as `columnKey: undefined`, and DataTable has to recover the
      // column from `field`. That recovery works; the divergence between two
      // derivations of the same key is the hazard, so say so at authoring time.
      if (col.key === undefined && comparatorFor(col) !== undefined) {
        console.warn(
          `DataTable: sortable column "${colKey}" has no \`key\`. antd reports a sort as ` +
            '`columnKey: column.key`, so DataTable falls back to `dataIndex`. Add an ' +
            'explicit `key` so the sort state and the column cannot key differently.',
        );
      }
    }
  }, [columns]);

  // 1. Drop capability-denied columns BEFORE useResizableColumns, so persisted
  //    widths — keyed by column key and re-mapped positionally — are not
  //    shifted onto a different column set.
  const permitted = useMemo(
    () => columns.filter((c) => c.cap === undefined || can(c.cap)),
    [columns, can],
  );

  const searchTerm = onSearch ? '' : (effective.search ?? '');

  // 6. Apply the search term. antd has no search concept, so this is ours.
  const searched = useMemo(() => {
    if (!searchTerm || !searchFields?.length) return rows as T[];
    const needle = searchTerm.toLowerCase();
    return (rows as T[]).filter((r) =>
      searchFields.some((f) => {
        const col = permitted.find((c) => c.dataIndex === f || c.key === f);
        const text = col
          ? cellTextFor(col, r)
          : String((r as Record<string, unknown>)[f] ?? '');
        return text.toLowerCase().includes(needle);
      }),
    );
  }, [rows, searchTerm, searchFields, permitted]);

  // 2-4. Apply kind defaults, controlled state, and click-propagation stops.
  const prepared = useMemo(
    () =>
      permitted.map((col) => {
        const colKey = stateKeyOf(col.key ?? col.dataIndex);
        const isActions = col.kind === 'actions';
        const excluded = isActions || (rowClickExcludeKeys ?? []).includes(colKey);
        const filterCfg = filterConfigFor(col, searched);
        const callerOnCell = col.onCell;

        const next: DataColumn<T> = {
          ...col,
          width: col.width ?? defaultWidthFor(col.kind),
          render: rendererFor(col),
          sorter: comparatorFor(col),
          sortOrder: effective.sort?.field === colKey ? effective.sort.order : null,
          ...(filterCfg
            ? {
                filters: filterCfg.filters.length ? filterCfg.filters : undefined,
                onFilter: filterCfg.onFilter,
                filteredValue: effective.filters?.[colKey] ?? null,
              }
            : {}),
        };
        // Strip per-column default sort: state is the single source now.
        delete (next as { defaultSortOrder?: unknown }).defaultSortOrder;

        // 4. Excluded columns (and the actions column) swallow the click so it
        //    never reaches the row handler. Compose with — never replace — a
        //    caller-supplied onCell.
        //
        //    onKeyDown is stopped alongside onClick because the row handler
        //    binds BOTH (Enter/Space activate a row for keyboard users).
        //    Stopping only the click left Space on a control inside an excluded
        //    cell firing the control AND the row navigation.
        if (excluded && onRowClick) {
          next.onCell = (record, index) => {
            const base = callerOnCell?.(record, index) ?? {};
            return {
              ...base,
              onClick: (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                (
                  base as { onClick?: (e: React.MouseEvent<HTMLElement>) => void }
                ).onClick?.(e);
              },
              onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                e.stopPropagation();
                (
                  base as {
                    onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
                  }
                ).onKeyDown?.(e);
              },
            };
          };
        }
        return next;
      }),
    [permitted, searched, effective, rowClickExcludeKeys, onRowClick],
  );

  // 5. Column resizing, applied last so it sees the final column set.
  const { columns: resizable, components } = useResizableColumns<T>(
    storageKey,
    prepared as NonNullable<TableProps<T>['columns']>,
  );

  // useResizableColumns REPLACES onHeaderCell rather than composing with it,
  // so a caller-supplied onHeaderCell would be silently dropped. Re-merge it
  // here, by position (useResizableColumns preserves order 1:1), applying the
  // same merge discipline used for onCell above. antd's own useSorter already
  // composes onto whatever onHeaderCell it receives, so the sort click and the
  // resize handle both survive.
  const finalColumns = useMemo(
    () =>
      resizable.map((col, i) => {
        const callerOnHeaderCell = prepared[i]?.onHeaderCell;
        if (!callerOnHeaderCell) return col;
        // Contextually typed by HeaderCellFn, so `column` carries exactly the
        // type both callbacks already accept — no cast needed at the call.
        const resizeOnHeaderCell = (col as { onHeaderCell?: HeaderCellFn<T> })
          .onHeaderCell;
        const merged: HeaderCellFn<T> = (column) => ({
          ...callerOnHeaderCell(column),
          ...(resizeOnHeaderCell?.(column) ?? {}),
        });
        return { ...col, onHeaderCell: merged };
      }) as NonNullable<TableProps<T>['columns']>,
    [resizable, prepared],
  );

  const handleChange: TableProps<T>['onChange'] = (pag, filters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : (sorter as SorterResult<T>);
    const nextFilters: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v && v.length) nextFilters[k] = v.map((x) => String(x));
    }
    // antd populates the sorter result strictly as `columnKey: column.key` and
    // `field: column.dataIndex` (antd/es/table/hooks/useSorter.js), so a column
    // defined with only a `dataIndex` — which antd's ColumnType permits, `key`
    // being optional — arrives here with `columnKey: undefined`. Requiring
    // `columnKey` made every click on such a column write `sort: undefined`
    // while its header still advertised `column-has-sorters`: the same silent
    // inertness the uncontrolled fallback exists to remove. Fall back to
    // `field`, normalised through the same `stateKeyOf` that produced `colKey`.
    const sortKey = stateKeyOf(s?.columnKey ?? s?.field);
    const nextSort =
      s?.order && sortKey ? { field: sortKey, order: s.order } : undefined;

    // The contract is that ANY narrowing change resets to page 1. antd only
    // does that for itself on a filter change — `onFilterChange` calls
    // `triggerOnChange(..., 'filter', true)`, whose `reset` flag forces
    // `pagination.current = 1`, while `onSorterChange` passes `reset = false`
    // (antd/es/table/InternalTable.js). So a sort applied from page 4 arrives
    // here still carrying `current: 4`. Search resets in `commitSearch`;
    // this is the sort half.
    const sortChanged =
      (nextSort?.field ?? null) !== (effective.sort?.field ?? null) ||
      (nextSort?.order ?? null) !== (effective.sort?.order ?? null);

    setTableState({
      ...effective,
      sort: nextSort,
      filters: Object.keys(nextFilters).length ? nextFilters : undefined,
      page: sortChanged ? 1 : (pag.current ?? 1),
      pageSize: pag.pageSize ?? DEFAULT_PAGE_SIZE,
    });
  };

  const clearAll = () => {
    setSearchDraft('');
    // This clear IS a commit by this component, so claim it — otherwise the
    // effect above reads the resulting empty `committed` as an external change.
    lastCommittedRef.current = '';
    // In server mode the term is not in table state, so clearing it means
    // telling the server — otherwise this button leaves the filter applied.
    if (onSearch) {
      setServerSearch('');
      onSearch('');
    }
    setTableState({
      ...effective,
      search: undefined,
      filters: undefined,
      page: 1,
    });
  };

  // Server mode narrows through `serverSearch`, not `effective.search`.
  // Omitting it meant a server search returning zero rows looked like a
  // genuinely empty resource and rendered `empty.action` ("No companies yet
  // — Create company") instead of "No results".
  const hasNarrowing =
    Boolean(onSearch ? serverSearch : searchTerm) ||
    Object.values(effective.filters ?? {}).some((v) => v.length > 0);

  const unfilteredEmpty = rows.length === 0;

  const searchBox =
    searchFields?.length || onSearch ? (
      <Input.Search
        allowClear
        placeholder={searchPlaceholder}
        value={searchDraft}
        style={{ maxWidth: 320 }}
        onChange={(e) => setSearchDraft(e.target.value)}
        // Enter / the search button bypass the debounce and commit at once.
        onSearch={commitSearch}
      />
    ) : null;

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {searchBox}
      <AsyncBoundary
        // Skeleton only on the first paint. `rows` is sticky (see above), so
        // during a refetch that blanks the raw data it stays populated — this
        // is false, the table stays on screen, and only a genuine initial load
        // (or a truly empty resource) shows the skeleton.
        loading={isLoading && rows.length === 0}
        error={error}
        onRetry={onRetry}
        skeleton="table"
        empty={unfilteredEmpty && !hasNarrowing}
        emptyTitle={empty?.title}
        emptyDescription={empty?.description}
        emptyAction={empty?.action}
      >
        <Table<T>
          dataSource={searched}
          columns={finalColumns}
          components={components}
          rowKey={rowKey as TableProps<T>['rowKey']}
          size={density === 'compact' ? 'small' : 'middle'}
          scroll={scrollX === false ? undefined : { x: scrollX }}
          onChange={handleChange}
          locale={{
            emptyText: hasNarrowing ? (
              <Space direction="vertical" size={8}>
                <span>No results match your filters.</span>
                <Button size="small" onClick={clearAll}>
                  Clear filters and search
                </Button>
              </Space>
            ) : undefined,
          }}
          pagination={
            pagination === false
              ? false
              : {
                  current: effective.page ?? 1,
                  pageSize:
                    pagination?.pageSize ?? effective.pageSize ?? DEFAULT_PAGE_SIZE,
                  showSizeChanger: pagination?.showSizeChanger ?? true,
                  pageSizeOptions: [25, 50, 100],
                  // `total` is deliberately omitted: antd derives it from the
                  // post-filter row set, which is the only correct value.
                }
          }
          onRow={
            onRowClick
              ? (record) => ({
                  onClick: () => onRowClick(record),
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(record);
                    }
                  },
                  tabIndex: 0,
                  role: 'link',
                  style: { cursor: 'pointer' },
                })
              : undefined
          }
        />
      </AsyncBoundary>
    </Space>
  );
}
